import { AsYouType, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/max'

/**
 * Formats a phone number progressively as the user types it, using
 * `AsYouType` for live input masking. Returns the raw input unchanged if it
 * cannot be formatted (never throws).
 */
export function formatPhoneAsYouType(input: string, defaultCountry?: string): string {
  try {
    const formatter = new AsYouType(defaultCountry as CountryCode | undefined)
    return formatter.input(input)
  } catch {
    return input
  }
}

/**
 * True iff `e164` is a valid, parseable phone number whose type is a mobile
 * number OR a number type that cannot be distinguished from mobile
 * (`FIXED_LINE_OR_MOBILE`, e.g. most US numbers). False for anything
 * definitively non-mobile (e.g. `FIXED_LINE`), unparseable, or invalid.
 * Never throws.
 */
export function isValidMobilePhone(e164: string): boolean {
  try {
    const phoneNumber = parsePhoneNumberFromString(e164)
    if (!phoneNumber || !phoneNumber.isValid()) return false
    const type = phoneNumber.getType()
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE'
  } catch {
    return false
  }
}

/**
 * Parses `input` (optionally relative to `defaultCountry` for national-format
 * input) and returns its E.164 representation, or `null` if it cannot be
 * parsed into a valid number. Never throws.
 */
export function toE164(input: string, defaultCountry?: string): string | null {
  try {
    const phoneNumber = parsePhoneNumberFromString(input, defaultCountry as CountryCode | undefined)
    if (!phoneNumber || !phoneNumber.isValid()) return null
    return phoneNumber.number
  } catch {
    return null
  }
}
